import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Seo from "@/components/Seo";
import {
  Users, MessageCircle, Compass, HandHeart, Leaf, Sparkles, Heart, BookOpen,
  Calendar, Music, Lock, UserCheck, Loader2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import CommunityTimeline from "@/components/CommunityTimeline";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import RichContent from "@/components/RichContent";
const HERO = "https://static.prod-images.emergentagent.com/jobs/cc147483-69b1-437e-8d93-352c289a737f/images/b67f28d37569c7bdfdf609a6c11ad2900ebd9112d7b255fa29c76d7c7f4d5fb0.png";

export const GROUP_ICONS = { Users, MessageCircle, Compass, HandHeart, Leaf, Sparkles, Heart, BookOpen, Calendar, Music };
export const GroupIcon = ({ name, ...props }) => {
  const Ic = GROUP_ICONS[name] || Users;
  return <Ic {...props} />;
};

const ACCESS_BADGE = {
  open: { label: "Open", cls: "bg-[#5C8A72]/15 text-[#5C8A72]" },
  tier_gated: { label: "Members", cls: "bg-[#144F75]/10 text-[#144F75]" },
  request: { label: "Request to join", cls: "bg-[#D98E73]/15 text-[#b8694f]" },
};

const features = [
  { icon: Users, title: "Member Profiles", text: "A presence in the community that reflects who you are." },
  { icon: MessageCircle, title: "Groups & Discussions", text: "Private groups, circles, and conversations by role and interest." },
  { icon: Calendar, title: "Event Discussions", text: "Prepare, connect, and reflect around each gathering." },
  { icon: BookOpen, title: "Teachings & Reflections", text: "Member-only teachings and shared reflections." },
];

function GroupCard({ group }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const badge = ACCESS_BADGE[group.access] || ACCESS_BADGE.open;

  const joinMut = useMutation({
    mutationFn: () => api.post(`/groups/${group.id}/join`),
    onSuccess: ({ data }) => {
      qc.invalidateQueries({ queryKey: ["groups"] });
      if (data.status === "pending") toast.success("Request sent — a host will approve you soon.");
      else { toast.success(`Welcome to ${group.name}!`); nav(`/community/groups/${group.slug}`); }
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail?.message || e.response?.data?.detail) || "Could not join"),
  });

  const isMember = group.my_status === "active";
  const isPending = group.my_status === "pending";

  const primary = () => {
    if (!user) { nav("/login"); return; }
    if (isMember) { nav(`/community/groups/${group.slug}`); return; }
    joinMut.mutate();
  };

  return (
    <div data-testid={`group-card-${group.slug}`} className="eyw-card overflow-hidden flex flex-col">
      <div className="relative h-36 bg-[#E5E0D8]">
        {group.image && <img src={group.image} alt={group.name} className="h-full w-full object-cover" />}
        <span className={`absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${badge.cls} bg-white/90`}>
          {group.access === "request" ? <Lock size={11} /> : group.access === "tier_gated" ? <UserCheck size={11} /> : null} {badge.label}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="grid place-items-center h-8 w-8 rounded-full bg-[#144F75]/10 text-[#144F75]"><GroupIcon name={group.icon} size={16} /></span>
          <h3 className="text-lg text-[#1A242B] leading-tight">{group.name}</h3>
        </div>
        <RichContent preview text={group.description} className="text-sm text-[#4A5D6B] mt-1 leading-relaxed flex-1" />
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-[#8FA89B]" data-testid={`group-members-${group.slug}`}>{group.member_count} {group.member_count === 1 ? "member" : "members"}</span>
          {isMember ? (
            <Link to={`/community/groups/${group.slug}`} data-testid={`group-open-${group.slug}`} className="eyw-btn !py-2 !px-4 text-sm">Open <ArrowRight size={14} /></Link>
          ) : isPending ? (
            <span className="text-xs text-[#b8694f] font-medium">Request pending</span>
          ) : (
            <button onClick={primary} disabled={joinMut.isPending} data-testid={`group-join-${group.slug}`} className="eyw-btn !py-2 !px-4 text-sm disabled:opacity-60">
              {joinMut.isPending && <Loader2 className="animate-spin" size={14} />} {group.access === "request" ? "Request" : "Join"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Community() {
  const { data: groups = [], isLoading } = useQuery({ queryKey: ["groups"], queryFn: () => api.get("/groups").then((r) => r.data) });

  return (
    <div data-testid="community-page">
      <Seo
        title="Wellness Community"
        description="Join circles, groups, workshops, and community practices. Connect with people on a shared path of growth, healing, and conscious participation in Newport, KY and online."
        image={HERO}
        path="/community"
      />
      <PageHero
        label="Community Participation"
        title="A living community, not a feed."
        subtitle="Profiles, circles, groups, and shared practices — a development environment where members practice their enhancements together."
        image={HERO}
        testid="community-hero"
      />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-[1fr_360px] gap-10 mb-16">
          <div>
            <p className="eyw-label mb-3">Community Timeline</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-[#1A242B] mb-6">What's alive right now.</h2>
            <CommunityTimeline />
          </div>
          <div className="lg:pt-2">
            <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-6">
              {features.map((f) => (
                <div key={f.title} className="eyw-card p-6">
                  <span className="grid place-items-center h-11 w-11 rounded-full bg-[#144F75]/10 text-[#144F75] mb-3"><f.icon size={20} /></span>
                  <h3 className="text-base text-[#1A242B] mb-1">{f.title}</h3>
                  <p className="text-[#4A5D6B] text-sm leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="eyw-label mb-3">Community Groups</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-[#1A242B]">Find your circle.</h2>
          </div>
          <Link to="/community/members" data-testid="members-directory-link" className="eyw-btn-ghost !py-2.5 text-sm"><Users size={16} /> Member directory</Link>
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-20 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="groups-grid">
            {groups.map((g) => <GroupCard key={g.id} group={g} />)}
          </div>
        )}

        <div className="mt-16 rounded-3xl bg-[#144F75] p-10 lg:p-14 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl text-white">Access grows with your membership.</h2>
          <p className="text-white/80 mt-3 max-w-2xl mx-auto">Discovery opens the door. Community unlocks private groups and included circles.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-7">
            <Link to="/join" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c97d63]">Join Discovery</Link>
            <Link to="/memberships" className="eyw-btn-ghost !border-white !text-white hover:!bg-white hover:!text-[#144F75]">See Memberships</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
